import httpClient from "./httpClient";

export const TABLE_SESSION_ENDPOINTS = {
  validatePublicTable: (publicTableCode) =>
    `/table-sessions/public/table/${encodeURIComponent(publicTableCode)}`,
  requestAccess: "/table-sessions/public/request",
  requestStatus: "/table-sessions/public/request/status",
  current: "/table-sessions/public/current",
  exitCurrent: "/table-sessions/public/current",
  activeOrder: "/table-sessions/public/order",
  createOrder: "/table-sessions/public/order",
  updateOrder: "/table-sessions/public/order",
  // Collaborative shared-order commands (real backend contract, smartpay-dashboard
  // src/app/routes/tableSessions.js). All require the participant cookie (validateTableSession);
  // the legacy full-replace order endpoints above stay untouched.
  addItem: "/table-sessions/public/order/items",
  submitItems: "/table-sessions/public/order/items/submit",
  itemByUuid: (lineUuid) => `/table-sessions/public/order/items/${encodeURIComponent(lineUuid)}`,
  // El cliente pide un descuento sobre una de sus lineas; lo resuelve el personal desde el POS (Mesas).
  itemDiscountRequest: (lineUuid) => `/table-sessions/public/order/items/${encodeURIComponent(lineUuid)}/discount-request`,
  // PRIMARY-only invite lifecycle. join()/get() are unauthenticated (the guest has no cookie yet).
  createInvite: "/table-sessions/public/invitations",
  inviteByToken: (token) => `/table-sessions/public/invitations/${encodeURIComponent(token)}`,
  joinInvite: (token) => `/table-sessions/public/invitations/${encodeURIComponent(token)}/join`,
  revokeInvite: (inviteUuid) => `/table-sessions/public/invitations/${encodeURIComponent(inviteUuid)}`,
  // Waiter-controlled payment gate, closing side: only succeeds once the waiter authorized payment
  // (POS Mesas/Precuentas) - see smartpay-dashboard controller/tableSessions.js#checkoutPublicOrder.
  // Gateway-initiation itself (Evertec/PowerTranz/Yappy) reuses the same existing endpoints the cart
  // checkout already calls (POST /evertech, /powertranz, /powertranz/payment, /btn/yappy) - no
  // table-specific wrappers for those, they already work as-is with the ecommerce public token.
  checkout: "/table-sessions/public/checkout",
  // Customer-initiated "solicitar pagar": notifies staff (POS "Mesas"/"Payments") that the table
  // wants to close out - does NOT itself authorize payment, that still requires staff action.
  requestPayment: "/table-sessions/public/request-payment",
  // See smartpay-dashboard services/tableSessions.js#attachEvertechTracking - Evertec's own return
  // redirect can only notify this browser via socket if it finds a TempOrder by evertech_code, so
  // this must be called (and awaited) before opening the PlaceToPay tab.
  checkoutEvertecTrack: "/table-sessions/public/checkout/evertec/track",
  // Same idea, for a whole-account Yappy btn payment - see smartpay-dashboard
  // services/tableSessions.js#attachYappyWholeAccountTracking. Lets Yappy's own IPN webhook finalize
  // the checkout server-side even if this tab never comes back after the customer pays.
  checkoutYappyTrack: "/table-sessions/public/checkout/yappy/track",
  // Pre-pay ("Generar QR para pedir y pagar" flow, single-use dynamic QR). validate previews without
  // consuming; consume burns the token and mints the same session/participant cookies the static-QR
  // request/approve flow mints once a waiter approves.
  prepayValidate: (token) => `/table-sessions/public/prepay/${encodeURIComponent(token)}/validate`,
  prepayConsume: (token) => `/table-sessions/public/prepay/${encodeURIComponent(token)}/consume`,
  // Must be called before ANY gateway is contacted (not just before checkout) - see
  // smartpay-dashboard services/tablePrepayTokens.js#beginPrepayPayment for why.
  prepayBeginPayment: "/table-sessions/public/prepay/begin-payment",
  // Server-verified total for the (now locked) order, computed the same way checkout will price it -
  // used instead of whatever total the browser happens to have in memory.
  prepayOrderCalculation: "/table-sessions/public/prepay/order/calculation",
  // Pre-pago partial payment (multi-participant redesign, smartpay-dashboard
  // services/tableSessionPayments.js). Any active participant on the shared account can claim/confirm/
  // release ANY line, not just their own - see that file for why this is a separate gate from the
  // owner-only addItem/updateSharedItem/removeSharedItem above.
  prepayClaimLines: "/table-sessions/public/prepay/order/lines/claim",
  prepayConfirmClaim: (paymentAttemptId) => `/table-sessions/public/prepay/order/lines/${encodeURIComponent(paymentAttemptId)}/confirm`,
  prepayReleaseClaim: (paymentAttemptId) => `/table-sessions/public/prepay/order/lines/${encodeURIComponent(paymentAttemptId)}/release`,
  // Same idea as checkoutYappyTrack above, scoped to one partial-payment claim - see
  // smartpay-dashboard services/tableSessionPayments.js#attachYappyTrackingToClaim.
  prepayTrackYappyClaim: (paymentAttemptId) => `/table-sessions/public/prepay/order/lines/${encodeURIComponent(paymentAttemptId)}/track-yappy`,
  // "Finalizar cuenta": closes/invoices the shared account once nothing is left owed; 409s with the
  // pending balance otherwise (see smartpay-dashboard services/tableSessions.js#buildFinalizeAccountPayload).
  prepayFinalize: "/table-sessions/public/finalize",
  // Links a loyalty phone (DonLee "Paisanitos") to the table's order before checkout, so it's visible
  // in the POS while the account is still open and survives a staff-side close - see
  // setSessionClientPhone in smartpay-dashboard services/tableSessions.js.
  orderClientPhone: "/table-sessions/public/order/client-phone",
  // "Personalizar factura": nombre/RUC/DV con los que se facturara la cuenta (ver
  // setSessionInvoiceData en smartpay-dashboard services/tableSessions.js).
  orderInvoiceData: "/table-sessions/public/order/invoice-data",
};

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);

const unwrap = (response) => {
  const body = response?.data;
  if (body && hasOwn(body, "data")) return body.data;
  return body || response;
};

const unwrapCurrentSession = (response) => {
  const body = response?.data;
  if (body && body.has_table_session === false) return body;
  return unwrap(response);
};

const normalizeStatusPayload = (payload) => (
  payload && typeof payload === "object"
    ? {
      ...payload,
      status: payload.status || payload.request_status || payload.requestStatus,
    }
    : payload
);

export class TableSessionsFetchAPI {
  static async validatePublicTable(publicTableCode) {
    const res = await httpClient(TABLE_SESSION_ENDPOINTS.validatePublicTable(publicTableCode), {
      method: "get",
    });
    return unwrap(res);
  }

  static async requestAccess({ publicTableCode, source, customer }) {
    const data = { public_table_code: publicTableCode };
    if (source === "QR" || source === "NFC") data.source = source;
    if (customer) data.customer = customer;

    const res = await httpClient(TABLE_SESSION_ENDPOINTS.requestAccess, {
      method: "post",
      data,
    });
    return normalizeStatusPayload(unwrap(res));
  }

  static async getRequestStatus() {
    const res = await httpClient(TABLE_SESSION_ENDPOINTS.requestStatus, {
      method: "get",
    });
    return normalizeStatusPayload(unwrap(res));
  }

  static async getCurrentSession() {
    const res = await httpClient(TABLE_SESSION_ENDPOINTS.current, {
      method: "get",
    });
    return unwrapCurrentSession(res);
  }

  static async exitCurrentSession() {
    const res = await httpClient(TABLE_SESSION_ENDPOINTS.exitCurrent, {
      method: "delete",
    });
    return unwrap(res);
  }

  static async getActiveOrder() {
    const res = await httpClient(TABLE_SESSION_ENDPOINTS.activeOrder, {
      method: "get",
    });
    return unwrap(res);
  }

  static async createTableOrder(data, idempotencyKey) {
    const res = await httpClient(TABLE_SESSION_ENDPOINTS.createOrder, {
      method: "post",
      data,
      headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : undefined,
    });
    return unwrap(res);
  }

  static async updateTableOrder(data, idempotencyKey) {
    const res = await httpClient(TABLE_SESSION_ENDPOINTS.updateOrder, {
      method: "put",
      data,
      headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : undefined,
    });
    return unwrap(res);
  }

  // ---- Shared-order item commands (Idempotency-Key is optional but recommended; the backend is
  // replay-safe only when it is sent). Every response is the sanitized shared-order snapshot
  // ({order_code, version, groups, combos, totals, participants_summary, updated_at}).

  static async addSharedItem({ type, item, expectedVersion } = {}, idempotencyKey) {
    const data = { type, item };
    if (expectedVersion !== undefined && expectedVersion !== null) data.expected_version = expectedVersion;

    const res = await httpClient(TABLE_SESSION_ENDPOINTS.addItem, {
      method: "post",
      data,
      headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : undefined,
    });
    return unwrap(res);
  }

  static async updateSharedItem(lineUuid, changes = {}, expectedVersion, idempotencyKey) {
    const data = { ...changes };
    if (expectedVersion !== undefined && expectedVersion !== null) data.expected_version = expectedVersion;

    const res = await httpClient(TABLE_SESSION_ENDPOINTS.itemByUuid(lineUuid), {
      method: "patch",
      data,
      headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : undefined,
    });
    return unwrap(res);
  }

  static async requestItemDiscount(lineUuid, reason, expectedVersion, idempotencyKey) {
    const data = { reason: reason || "" };
    if (expectedVersion !== undefined && expectedVersion !== null) data.expected_version = expectedVersion;

    const res = await httpClient(TABLE_SESSION_ENDPOINTS.itemDiscountRequest(lineUuid), {
      method: "post",
      data,
      headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : undefined,
    });
    return unwrap(res);
  }

  static async removeSharedItem(lineUuid, expectedVersion, idempotencyKey) {
    const data = {};
    if (expectedVersion !== undefined && expectedVersion !== null) data.expected_version = expectedVersion;

    const res = await httpClient(TABLE_SESSION_ENDPOINTS.itemByUuid(lineUuid), {
      method: "delete",
      data,
      headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : undefined,
    });
    return unwrap(res);
  }

  static async submitSharedItems(lineUuids, expectedVersion, idempotencyKey) {
    const data = {};
    if (Array.isArray(lineUuids) && lineUuids.length > 0) data.line_uuids = lineUuids;
    if (expectedVersion !== undefined && expectedVersion !== null) data.expected_version = expectedVersion;

    const res = await httpClient(TABLE_SESSION_ENDPOINTS.submitItems, {
      method: "post",
      data,
      headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : undefined,
    });
    return unwrap(res);
  }

  // ---- Invitations (PRIMARY-only create/revoke; validate/join are unauthenticated by nature).

  static async createInvite() {
    const res = await httpClient(TABLE_SESSION_ENDPOINTS.createInvite, { method: "post" });
    return unwrap(res);
  }

  static async getInvite(token) {
    const res = await httpClient(TABLE_SESSION_ENDPOINTS.inviteByToken(token), { method: "get" });
    return unwrap(res);
  }

  static async joinInvite(token, displayName) {
    const res = await httpClient(TABLE_SESSION_ENDPOINTS.joinInvite(token), {
      method: "post",
      data: { display_name: displayName },
    });
    return unwrap(res);
  }

  static async revokeInvite(inviteUuid) {
    const res = await httpClient(TABLE_SESSION_ENDPOINTS.revokeInvite(inviteUuid), { method: "delete" });
    return unwrap(res);
  }

  // Creates the final order and closes this table's account. Called once a payment gateway
  // (Yappy/PowerTranz/Evertec/credit) confirms the charge - paymentDetails carries whatever that
  // gateway's success response needs on the final order (paymethod, paymenthod_integration_id,
  // EMVStreamResponse, yappy_transaction_id, etc. - see controller.newOrder on the backend).
  static async checkoutTableSession(paymentDetails) {
    const res = await httpClient(TABLE_SESSION_ENDPOINTS.checkout, {
      method: "post",
      data: paymentDetails,
    });
    return unwrap(res);
  }

  static async requestPayment() {
    const res = await httpClient(TABLE_SESSION_ENDPOINTS.requestPayment, { method: "post" });
    return unwrap(res);
  }

  static async setClientPhone(client_phone) {
    const res = await httpClient(TABLE_SESSION_ENDPOINTS.orderClientPhone, {
      method: "patch",
      data: { client_phone },
    });
    return unwrap(res);
  }

  // { client_name, ruc, dv }. Todo vacio quita la personalizacion (factura a consumidor final).
  static async setInvoiceData({ client_name, ruc, dv }) {
    const res = await httpClient(TABLE_SESSION_ENDPOINTS.orderInvoiceData, {
      method: "patch",
      data: { client_name, ruc, dv },
    });
    return unwrap(res);
  }

  // Must be called (and awaited) before opening the Evertec/PlaceToPay tab - see
  // TABLE_SESSION_ENDPOINTS.checkoutEvertecTrack above for why.
  static async trackEvertechPayment({ evertech_code, evertech_transaction_id }) {
    const res = await httpClient(TABLE_SESSION_ENDPOINTS.checkoutEvertecTrack, {
      method: "post",
      data: { evertech_code, evertech_transaction_id },
    });
    return unwrap(res);
  }

  // Must be called (and awaited) before handing the transactionId to the <btn-yappy> widget's
  // eventPayment - see TABLE_SESSION_ENDPOINTS.checkoutYappyTrack above for why (whole-account).
  static async trackYappyPayment(details) {
    const res = await httpClient(TABLE_SESSION_ENDPOINTS.checkoutYappyTrack, {
      method: "post",
      data: details,
    });
    return unwrap(res);
  }

  // Partial/individual-products counterpart of trackYappyPayment above.
  static async trackYappyClaim(paymentAttemptId, details) {
    const res = await httpClient(TABLE_SESSION_ENDPOINTS.prepayTrackYappyClaim(paymentAttemptId), {
      method: "post",
      data: details,
    });
    return unwrap(res);
  }

  // ---- Pre-pay (single-use dynamic QR). ----

  static async validatePrepayToken(token) {
    const res = await httpClient(TABLE_SESSION_ENDPOINTS.prepayValidate(token), { method: "get" });
    return unwrap(res);
  }

  static async consumePrepayToken(token, customer) {
    const res = await httpClient(TABLE_SESSION_ENDPOINTS.prepayConsume(token), {
      method: "post",
      data: customer ? { customer } : undefined,
    });
    return unwrap(res);
  }

  static async beginPrepayPayment() {
    const res = await httpClient(TABLE_SESSION_ENDPOINTS.prepayBeginPayment, { method: "post" });
    return unwrap(res);
  }

  static async getPrepayOrderCalculation() {
    const res = await httpClient(TABLE_SESSION_ENDPOINTS.prepayOrderCalculation, { method: "get" });
    return unwrap(res);
  }

  // ---- Pre-pago partial payment (claim a selection of lines, confirm/release one attempt). ----

  // Quotes AND locks the given lines in one call - returns {payment_attempt_id, calculation_id,
  // subtotal, total, tax, discount, order}. The gateway is only ever opened after this succeeds.
  // channel: omit for the usual gateway-backed claim; "POS_PHYSICAL" for "Pagar con POS" - makes the
  // backend notify the waiter's POS in real time instead of expecting an online gateway charge next
  // (see smartpay-dashboard services/tableSessionPayments.js#claimLinesForPayment).
  static async claimPrepayLines(lineUuids, channel) {
    const res = await httpClient(TABLE_SESSION_ENDPOINTS.prepayClaimLines, {
      method: "post",
      data: { line_uuids: lineUuids, ...(channel ? { channel } : {}) },
    });
    return unwrap(res);
  }

  // Called once the gateway confirms the charge for that specific claim - paymentDetails must include
  // calculation_id (the one returned by claimPrepayLines) plus whatever the gateway's own success
  // response carries (paymethod_id, paymenthod_integration_id, gateway_name, gateway_reference/...).
  static async confirmPrepayClaim(paymentAttemptId, paymentDetails) {
    const res = await httpClient(TABLE_SESSION_ENDPOINTS.prepayConfirmClaim(paymentAttemptId), {
      method: "post",
      data: paymentDetails,
    });
    return unwrap(res);
  }

  // Called when the gateway attempt fails/is cancelled, so the claimed lines become payable again
  // immediately instead of waiting out the backend's own claim TTL.
  static async releasePrepayClaim(paymentAttemptId, reason) {
    const res = await httpClient(TABLE_SESSION_ENDPOINTS.prepayReleaseClaim(paymentAttemptId), {
      method: "post",
      data: reason ? { reason } : undefined,
    });
    return unwrap(res);
  }

  // "Finalizar cuenta": succeeds (creates the final invoice, closes the session) only once nothing is
  // left owed - throws with the backend's 409 {code:'PENDING_BALANCE', pending_balance, unpaid_line_uuids}
  // otherwise, which the caller should use to route the customer to pay the rest instead of retrying.
  static async finalizePrepayAccount() {
    const res = await httpClient(TABLE_SESSION_ENDPOINTS.prepayFinalize, { method: "post" });
    return unwrap(res);
  }
}
